import type { LanguageLocale } from "utils/language"
import type { StoreFrontResponse } from "mobx/AddressManager/types"

export interface Configuration {
    orderTypeFlows: OrderTypeFlow[],
    canSkipOrderTypeSelection: boolean,
    displayToMenuButton: boolean,
    enabled: boolean
}

export interface Localization {
    actualFlow: LocalizationFlow | null | undefined
    processingFlow: LocalizationFlow | null | undefined

    orderTypeFlows: OrderTypeFlow[]
    setOrderTypeFlows: (orderTypeFlows: OrderTypeFlow[]) => void

    addressDetailsTypes: AddressDetails[]
    setAddressDetailsTypes: (addressDetailsTypes: AddressDetails[]) => void

    savedAddress: Address[]
    addToSavedAddress: (address: Address) => void
    removeSavedAddress: (addressId: string) => void

    started: boolean | undefined
    currentStep: FlowStep | null
    alreadyLocalized: boolean | undefined

    start: () => void
    abort: () => void
    end: () => void
    nextStep: () => void
    previousStep: () => void
    goToStep: (step: FlowStep) => void
    canGoToStep: (step: FlowStep) => boolean

    getValidateAddress(address: Address): Promise<any> | undefined
    goToLocalizedMenu: () => void
    getLocalizedMenuPath: () => string | undefined

    config: () => Configuration | undefined

    skipOrderTypeSelection: () => Promise<boolean> | undefined

    forceLocalizationToStore: (storeId: string) => void
}

export class LocalizationFlow {
	constructor() {
	}

    orderTypeFlow: OrderTypeFlow | null | undefined = null

    setOrderTypeFlow = (orderTypeFlow: OrderTypeFlow) => {
        this.orderTypeFlow = orderTypeFlow
    }

    setOrderTypeFlowDisplayAvailableStores = (displayAvailableStores: boolean) => {
        if(this.orderTypeFlow) {
            this.orderTypeFlow = {
                ...this.orderTypeFlow,
                displayAvailableStores
            }
        }
    }

    store: Store | null | undefined = null

    setStore = (store: Store) => {
        this.store = store
    }

    address: Address | null | undefined = null

    setAddress = (address: Address) => {
        this.address = address
    }

    setAddressLocation = (formatted: string, location: Location) => {
        if(this.address) {
            this.address = {
                ...this.address,
                formatted,
                location
            }
        }
    }

    setAddressDetails = (details: AddressDetails) => {
        if(this.address) {
            this.address = {
                ...this.address,
                details
            }
        }
    }
}

export interface LocalizationDependencies {
    getStores: () => Promise<{deliveryStores: boolean, disablePickup: boolean, pickupStores: []}>
    getStoresOnAddress: (formatted: string, lat: number, lng: number) => Promise<[]>

    clearCouponToApply: () => void

    save(flow: LocalizationFlow): void
    get(): LocalizationFlow | null
    config(): Configuration | undefined

    initSessionAndLoadMenu(storeId: string): Promise<boolean>
    initSession(storeId?: string): Promise<string>

    getValidateAddress(address: Address): Promise<any>

    goToLocalizedMenu: () => Promise<void>
    getLocalizedMenuPath: () => string | undefined

    getStoreDetailsById(storeId: string): Promise<StoreFrontResponse>
    checkPickupAvailabilityByStore(store: StoreFrontResponse): void
    
    sendLocalizationStartEvent(): void
    sendLocalizationFinishEvent(storeId: string, orderType: string): void
}

export interface OrderTypeFlow {
    orderType: OrderType,
    orderTypeTranslationKey: string,
    orderTypeImageSrc: string,
    steps: FlowStep[],
    requireAddress: boolean
    displayAvailableStores?: boolean
}

export interface Store {
    id: string
    name: Partial<Record<LanguageLocale, string>>
    address: string
}

export enum FlowStep {
    OrderTypeSelection = 'OrderTypeSelection',
    AddressSelection = 'AddressSelection',
    AddressDetailsSelection = 'AddressDetailsSelection',
    StoreOnMapSelection = 'StoreOnMapSelection'
}

export interface Address {
    id: string | null
    formatted: string
    location: Location,
    details : AddressDetails | null
    components: google.maps.GeocoderAddressComponent[]
}

export interface AddressDetails {
    type: AddressType
    fields: Field[]
}

export interface Location {
    lat: number,
    lng: number
}

export enum OrderType {
    Delivery = 'delivery',
    Pickup = 'pickup'
}

enum AddressType {
    Apartment = 'Apartment',
    Office = 'Office',
    House = 'House',
    Others = 'Others'
}

interface Field {
    label: string
    translatedKey: string
}

const addressDetailsFields : AddressDetails[] = [
    {
        type: AddressType.Apartment,
        fields : [
            {label: 'Floor', translatedKey: 'xxx'},
            {label: 'Apt number', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.Office,
        fields : [
            {label: 'Floor', translatedKey: 'xxx'},
            {label: 'Company name', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.House,
        fields : [
            {label: 'Doorbell', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.Others,
        fields : [
            {label: 'Type of location', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    }

]

/*

type AddressDetailsTypes = {
    [key in AddressType]?: Field[]
}

// Need to check if this should be saved in database, or hardcoded
const addressDetailsFields : AddressDetailsTypes = {
    [AddressType.Apartment]: [
        {label: 'Floor', translatedKey: 'xxx'},
        {label: 'Apt number', translatedKey: 'xxx'},
        {label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [AddressType.Office]: [
        {label: 'Floor', translatedKey: 'xxx'},
        {label: 'Company name', translatedKey: 'xxx'},
        {label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [AddressType.House]: [
        {label: 'Doorbell', translatedKey: 'xxx'},
        {label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [AddressType.Others]: [
        {label: 'Type of location', translatedKey: 'xxx'},
        {label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
}
*/

/*
type OrderTypesFlows = {
    [key in OrderType]?: {
        steps: FlowStep[],
        requireAddress: boolean
    }
}

const addressDetailsFields2 : AddressDetails[] = [
    {
        type: AddressType.Apartment,
        fields : [
            {label: 'Floor', translatedKey: 'xxx'},
            {label: 'Apt number', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.Office,
        fields : [
            {label: 'Floor', translatedKey: 'xxx'},
            {label: 'Company name', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.House,
        fields : [
            {label: 'Doorbell', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.Others,
        fields : [
            {label: 'Type of location', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    }

] */
